/**
 * LOT 1 perf — lazy video for the "carbon-product-narrative" section.
 *
 * Initial HTML ships NO <source src>: the MP4 URLs live in data attributes.
 *  1. approach observer (~800px before the section)  -> inject <source>, video.load()
 *  2. visibility observer (section really on screen) -> video.play()
 * The responsive poster <img> stays underneath; the video only fades in on "playing".
 * Anything failing (no IntersectionObserver, autoplay refused, network error) leaves the poster.
 */
class CarbonNarrativeVideo extends HTMLElement {
  #video = null;
  #approachObserver = null;
  #visibilityObserver = null;
  #loaded = false;

  static APPROACH_MARGIN = '800px 0px';
  static VISIBLE_THRESHOLD = 0.35;

  connectedCallback() {
    this.#video = this.querySelector('video');

    if (!this.#video || !this.dataset.videoSrc) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Degraded mode: poster only, no network cost, no broken player.
      return;
    }

    this.#video.addEventListener('playing', () => this.classList.add('is-playing'));
    this.#video.addEventListener('error', () => this.classList.remove('is-playing'), true);

    this.#approachObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.#load();
        this.#approachObserver?.disconnect();
        this.#approachObserver = null;
      },
      { rootMargin: CarbonNarrativeVideo.APPROACH_MARGIN, threshold: 0 }
    );

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.#play();
        this.#visibilityObserver?.disconnect();
        this.#visibilityObserver = null;
      },
      { rootMargin: '0px', threshold: CarbonNarrativeVideo.VISIBLE_THRESHOLD }
    );

    this.#approachObserver.observe(this);
    this.#visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    this.#approachObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    this.#approachObserver = null;
    this.#visibilityObserver = null;
  }

  #pickSource() {
    const sd = this.dataset.videoSrc;
    const hd = this.dataset.videoSrcHd || sd;
    const connection = navigator.connection;
    const prefersLight = connection?.saveData || /2g|3g/.test(connection?.effectiveType || '');
    const largeScreen = window.matchMedia('(min-width: 1000px)').matches;

    return largeScreen && !prefersLight ? hd : sd;
  }

  #load() {
    if (this.#loaded) {
      return;
    }

    this.#loaded = true;

    const source = document.createElement('source');
    source.src = this.#pickSource();
    source.type = this.dataset.videoType || 'video/mp4';
    this.#video.appendChild(source);
    this.#video.load();
  }

  #play() {
    this.#load();

    let playPromise;

    try {
      playPromise = this.#video.play();
    } catch (error) {
      return;
    }

    if (playPromise && typeof playPromise.catch === 'function') {
      // Autoplay refused or source failed: keep the poster, stay silent.
      playPromise.catch(() => {});
    }
  }
}

if (!customElements.get('carbon-narrative-video')) {
  customElements.define('carbon-narrative-video', CarbonNarrativeVideo);
}
